import React, {useCallback, useEffect, useState} from "react";
import {Row} from "reactstrap";
import {ReactComponent as BlogDateIcon} from "../../../../../../../assets/images/content/blog-date-icon.svg";
import {ReactComponent as BlogViewsIcon} from "../../../../../../../assets/images/content/blog-views-icon.svg";
import classnames from "classnames";
import Api from "../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ArrowBack} from "@mui/icons-material";
import ShareContent from "../../../../../../components/app-specific/content/share";
import LazyImage from "../../../../../../components/app-specific/lazy-image";
import ContentBlog from "../../../../../../components/app-specific/content/blog";
import {Paper} from "@mui/material";
import ContentRelatedTopicsSlider from "../../../../../../components/base/slider/content-related-topics";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../../core/services/date-utils";
import {useHistory, useParams} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";

const ContentBlogInformationView = () => {
    const params = useParams();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [loadingRelatedBlogs, setLoadingRelatedBlogs] = useState(false);
    const [blog, setBlog] = useState({});
    const [relatedBlogs, setRelatedBlogs] = useState([]);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('content-blog-information-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.contentBlogInformation(params.slug));
        configureBehavioralTool((tags) => tags.contentBlogInformation(params.slug));
    }, [configureBehavioralTool, configureNotebookTags, params.slug]);


    /**
     * Scrolls to the top of the page as soon as the slug changes
     */
    useEffect(() => {
        const mainSection = document.querySelector('main');
        mainSection?.scrollTo({left: 0, top: 0, behavior: 'smooth'})
    }, [params.slug])

    /**
     * Loads the related blogs of the current blog by searching with blogs' tags and if the result of the api is
     * successful, sets the data
     */
    const getRelatedBlogs = useCallback(async (blog) => {
        setLoadingRelatedBlogs(true);
        const forApi = {
            paginationInfo: {currentPage: 1, pageSize: 20},
            filters: {tagsId: blog.tags?.map(e => e.id)}
        };
        const response = await Api.searchBlogs(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setRelatedBlogs(response.data?.resultList?.filter(e => e.id !== blog.id) ?? []);
        }
        setLoadingRelatedBlogs(false)
    }, [isMounted])

    /**
     * Loads the full information of the specified blog based on its slug and if the result of the api is
     * successful, sets the data
     */
    const getFullInformation = useCallback(async () => {
        setLoading(true);
        const response = await Api.getBlogBySlug(params.slug);
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag && response.data) {
            setBlog(response.data);
            void getRelatedBlogs(response.data);
        }
    }, [params.slug, isMounted, getRelatedBlogs])

    /**
     * As soon as the component mounts, it calls the function for getting the full information of the blog
     */
    useEffect(() => {
        void getFullInformation();
    }, [getFullInformation])

    return (
        <>
            <Row className={'justify-content-around align-items-start px-4 content-single'}>
                {
                    loading
                        ? (
                            <>
                                <div className={'d-flex w-100 justify-content-between align-items-start'}>
                                    <div className={'d-flex flex-column w-50 w-lg-25'}>
                                        <div className={'loading extraSmall mb-2'}>
                                            <div/>
                                        </div>
                                        <div className={'loading extraExtraSmall'}>
                                            <div/>
                                        </div>
                                    </div>
                                    <div className={'w-10'}>
                                        <div className={'loading extraExtraSmall'}>
                                            <div/>
                                        </div>
                                    </div>
                                </div>
                                <div className={'w-100 mt-4'}/>
                                <div className={'w-100 loading blog-main-image mb-4'}>
                                    <div/>
                                </div>
                                <div className={'d-flex w-100'}>
                                    <div className={'w-50 loading extraSmall mb-5'}>
                                        <div/>
                                    </div>
                                </div>

                                <div className={'w-100 loading extraExtraSmall'}>
                                    {
                                        Array(20).fill(null).map((e, index) => (
                                            <div key={index}/>
                                        ))
                                    }
                                </div>
                            </>
                        )
                        : (
                            <>
                                <div className={'d-flex w-100 justify-content-between align-items-start'}>
                                    <div className={'d-flex flex-column w-75'}>
                                        <div className={'d-flex w-100 justify-content-start' +
                                            ' align-items-center'}>
                                            <IconButton

                                                onPressed={() => history.goBack()}
                                                color={'paper.secondary'}
                                            >
                                                <ArrowBack className='icon normal'/>
                                            </IconButton>
                                            <h2 className={'title m-0 ml-3'}>
                                                {blog.title ?? ''}
                                            </h2>
                                        </div>
                                        <div className={'d-flex w-100 justify-content-start' +
                                            ' align-items-center mb-1 ml-4'}>
                                            <p className={'date ml-4 mr-4'}>
                                                <BlogDateIcon className={'mr-1 ml-1'}/>
                                                {
                                                    blog?.lastModifyDateTime
                                                        ? DateUtils.serverToLocal(blog?.lastModifyDateTime).format('DD MMM YYYY')
                                                        : ''
                                                }
                                            </p>
                                            <p className={'date'}>
                                                <BlogViewsIcon className={'mr-2'}/>
                                                {`${blog.viewCount ?? 0}`}
                                            </p>
                                        </div>

                                    </div>
                                    <ShareContent title={blog?.slug} body={'Here\'s the link for this blog:'}/>
                                </div>
                                <div className={'w-100 mt-4'}/>
                                <div className={'blog-image mb-4 mx-3'}>
                                    <LazyImage className={'image'}
                                               src={blog?.coverImage?.srcUrl}
                                               alt={blog?.slug}
                                    />
                                </div>
                                <div className={'w-100 mt-3'}/>
                                <Paper className={'paper w-100 flex-wrap px-4 pb-5'}>
                                    <div className={'w-100 mx-5 mt-2'}>
                                        {
                                            blog?.otherPropValues?.length > 0 &&
                                            <>
                                                <div className={'w-100 mt-4'}/>
                                                <p className={'features'}>Blog's features:</p>
                                                <div className={'w-100 mb-3'}/>
                                            </>
                                        }
                                    </div>
                                    <div className={'d-flex flex-wrap w-100 mt-4 mx-5'}>
                                        {
                                            blog?.otherPropValues?.map((e, index) => (
                                                <div
                                                    key={e.property?.name ?? index}
                                                    className={classnames('blog-property-box', {'no-border': index === blog?.otherPropValues?.length - 1})}>
                                                    <div className={classnames('ball')}/>

                                                    <div className={'d-flex flex-column flex-wrap'}>
                                                        <h4 className={'title mb-1'}>
                                                            {e?.property?.name ?? ''}
                                                        </h4>
                                                        <p className={'value'}>
                                                            {e?.value ?? ''}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </div>
                                    <div className={'w-100 my-2'}/>
                                    <div className={'w-100 body mx-3'}
                                         dangerouslySetInnerHTML={{__html: blog?.bodyContent ?? ''}}
                                    />
                                </Paper>
                            </>
                        )
                }
                <Paper className={'paper flex-wrap p-4 pb-3 my-4 w-100'}>
                    <div className={'w-100 mx-3 mb-3'}>
                        <h2 className={'title'}>
                            Related Topics
                        </h2>
                    </div>
                    {
                        loadingRelatedBlogs
                            ? <div className={'w-100 d-flex flex-row p-0'} style={{overflowX: 'scroll'}}>
                                {
                                    Array(8).fill(null).map((e, index) => (
                                        <div className={'loading blog-loading'}>
                                            <div/>
                                        </div>
                                    ))
                                }
                            </div>
                            : <div className={'related-topics'}>
                                <ContentRelatedTopicsSlider
                                    content={relatedBlogs.map((blog) => (
                                        <ContentBlog key={blog.id} data={blog}/>
                                    ))}
                                />
                            </div>
                    }
                </Paper>
            </Row>
        </>
    );
}


export default ContentBlogInformationView;
